import { IOrderTransaction } from "redux/reducers/models/order";

/**
 * Net money collected on an order (active sale/capture minus refunds).
 * Used to warn before void/cancel when funds still need to be returned.
 */
export function getOrderNetCollected(transactions?: IOrderTransaction[] | null): number {
  if (!transactions?.length) {
    return 0;
  }

  const active = transactions.filter(transaction => !transaction.voided_at);
  const sales = active
    .filter(transaction => transaction.kind === "sale" || transaction.kind === "capture")
    .reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
  const refunds = active
    .filter(transaction => transaction.kind === "refund")
    .reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);

  return Math.round((sales - refunds) * 100) / 100;
}

export function orderHasCollectedPayments(
  financialStatus?: string | null,
  transactions?: IOrderTransaction[] | null,
  balance?: number | null,
  totalPrice?: number | null,
): boolean {
  // Prefer ledger when present — net collected is the source of truth
  if (transactions?.length) {
    return getOrderNetCollected(transactions) > 0;
  }

  if (!financialStatus || ["unpaid", "cancelled", "voided", "refunded"].includes(financialStatus)) {
    return false;
  }

  // Fallback when transactions are not loaded but status/balance show money moved
  if (typeof balance === "number" && typeof totalPrice === "number") {
    return balance < totalPrice;
  }

  return ["partially_paid", "paid", "partially_refunded"].includes(financialStatus);
}

export interface IRefundablePaymentTransaction {
  parent_id: number;
  amount: number;
  payment_method: string;
  payment_type: string;
  payment_intent_id?: string | null;
  processing_type?: string | null;
  source?: string | null;
}

/**
 * Remaining refundable amount per sale/capture tender (excludes voided rows).
 */
export function getRefundablePaymentTransactions(
  transactions?: IOrderTransaction[] | null,
): IRefundablePaymentTransaction[] {
  if (!transactions?.length) {
    return [];
  }

  const active = transactions.filter(transaction => !transaction.voided_at);
  const parents = active.filter(
    transaction => transaction.kind === "sale" || transaction.kind === "capture",
  );

  return parents
    .map(parent => {
      const alreadyRefunded = active
        .filter(transaction => transaction.kind === "refund" && Number(transaction.parent_id) === Number(parent.id))
        .reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
      const amount = Math.round((Number(parent.amount || 0) - alreadyRefunded) * 100) / 100;

      return {
        parent_id: parent.id,
        amount,
        payment_method: parent.payment_method,
        payment_type: parent.payment_type,
        payment_intent_id: parent.payment_intent_id,
        processing_type: parent.processing_type,
        source: parent.source,
      };
    })
    .filter(transaction => transaction.amount > 0);
}
